import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../components/api/axiosInstance";
import "../assets/css/AddLead.css";
import "../assets/css/Issues.css";
import "../assets/css/Permissions.css";
import { FaArrowLeft, FaChevronDown } from "react-icons/fa";
import { getAuthItem } from "../utils/authStorage";
import { formatPascal } from "../utils/formatters";
import { formValueWithMobileSanitize, mobileValidationMessage } from "../utils/phone";

function safeString(v) {
  return v == null ? "" : String(v);
}

function getCreatedBy() {
  try {
    const userStr = getAuthItem("user");
    if (!userStr) return 1;
    const user = JSON.parse(userStr);
    return user.user_id || user.id || 1;
  } catch {
    return 1;
  }
}

function getId(v) {
  if (v == null || v === "") return null;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? null : n;
}

function parseDecimal(v) {
  if (v == null || v === "") return null;
  const s = String(v).trim().replace(/,/g, ".");
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
}

function AddJobLead({ onBack, onAddLead }) {
  const createdBy = getCreatedBy();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    altPhone: "",
    qualificationId: "",
    experienceYears: "",
    currentCompany: "",
    designationId: "",
    expectedSalary: "",
    currentSalary: "",
    skills: "",
    preferredLocation: "",
    sourceId: "",
    statusId: "",
    assignedTo: "",
    addressLine1: "",
    addressLine2: "",
    cityId: "",
    stateId: "",
    countryId: "",
    zipCode: "",
  });

  const [phoneCountryCode, setPhoneCountryCode] = useState("+91");
  const [resumeFile, setResumeFile] = useState(null);

  const [roles, setRoles] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [showRolesDropdown, setShowRolesDropdown] = useState(false);

  const [qualifications, setQualifications] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const phoneCountryOptions = useMemo(() => {
    return (countries || []).map((c) => ({
      id: c.country_id ?? c.Country_Id ?? c.id,
      code: (c.country_code || c.country_short_code || "").trim(),
      name: c.country_name || c.Country_Name || c.name || "",
    }));
  }, [countries]);


  // Fetch active DigiX staff users for Assigned To
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = await axiosInstance.get("/users", { params: { status: "Active" } });
        const list = Array.isArray(response.data) ? response.data : [];
        const mapped = list
          .map((u) => ({
            id: u.user_id ?? u.User_Id ?? u.id,
            name: [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.email || `User #${u.user_id}`,
          }))
          .filter((u) => u.id != null)
          .sort((a, b) => a.name.localeCompare(b.name));
        setUsers(mapped);
        let currentId = "";
        try {
          const userStr = getAuthItem("user");
          if (userStr) {
            const user = JSON.parse(userStr);
            currentId = String(user.user_id ?? user.id ?? "");
          }
        } catch {
          /* ignore */
        }
        setFormData((prev) => {
          if (prev.assignedTo) return prev;
          const fallback = currentId || (mapped[0] ? String(mapped[0].id) : "");
          return { ...prev, assignedTo: fallback };
        });
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [
          rolesRes,
          qualRes,
          desRes,
          sourceRes,
          statusRes,
          countriesRes,
        ] = await Promise.all([
          axiosInstance.get("/roles"),
          axiosInstance.get("/qualifications"),
          axiosInstance.get("/designations"),
          axiosInstance.get("/lead-sources"),
          axiosInstance.get("/lead-statuses"),
          axiosInstance.get("/countries"),
        ]);

        const rawRoles = Array.isArray(rolesRes.data)
          ? rolesRes.data
          : Array.isArray(rolesRes.data?.data)
            ? rolesRes.data.data
            : [];
        setRoles(
          rawRoles
            .map((r) => ({
              id: r.role_id ?? r.Role_Id ?? r.id,
              name: r.role_name ?? r.Role_Name ?? r.name ?? "",
            }))
            .filter((r) => r.id != null && String(r.name).trim() !== "")
        );

        const rawQuals = Array.isArray(qualRes.data)
          ? qualRes.data
          : Array.isArray(qualRes.data?.data)
            ? qualRes.data.data
            : [];
        setQualifications(
          rawQuals
            .map((q) => ({
              id: q.Qualification_Id ?? q.qualification_id ?? q.id,
              name: q.Qualification_Name ?? q.qualification_name ?? q.name ?? "",
            }))
            .filter((q) => q.id != null && String(q.name).trim() !== "")
        );

        const rawDesignations = Array.isArray(desRes.data)
          ? desRes.data
          : Array.isArray(desRes.data?.data)
            ? desRes.data.data
            : [];
        setDesignations(
          rawDesignations
            .map((d) => ({
              id: d.Designation_Id ?? d.designation_id ?? d.id,
              name: d.Designation_Name ?? d.designation_name ?? d.name ?? "",
            }))
            .filter((d) => d.id != null && String(d.name).trim() !== "")
        );

        const rawSources = Array.isArray(sourceRes.data)
          ? sourceRes.data
          : Array.isArray(sourceRes.data?.data)
            ? sourceRes.data.data
            : [];
        setSources(
          rawSources
            .map((s) => ({
              id: s.Source_Id ?? s.source_id ?? s.id,
              name: s.Source_Name ?? s.source_name ?? s.name ?? "",
            }))
            .filter((s) => s.id != null && String(s.name).trim() !== "")
        );

        const rawStatuses = Array.isArray(statusRes.data)
          ? statusRes.data
          : Array.isArray(statusRes.data?.data)
            ? statusRes.data.data
            : [];
        const mappedStatuses = rawStatuses
            .map((s) => ({
              id: s.Status_Id ?? s.status_id ?? s.id,
              name: s.Status ?? s.status ?? s.name ?? "",
            }))
            .filter((s) => s.id != null && String(s.name).trim() !== "");
        setStatuses(mappedStatuses);
        const newStatus = mappedStatuses.find((s) => s.name.toLowerCase() === "new");
        if (newStatus) setFormData((p) => ({ ...p, statusId: String(newStatus.id) }));

        const rawCountries = Array.isArray(countriesRes.data)
          ? countriesRes.data
          : Array.isArray(countriesRes.data?.data)
            ? countriesRes.data.data
            : [];
        setCountries(rawCountries);
      } catch (err) {
        console.error("Error fetching job lead lookups:", err);
        // Keep going so user can still try; missing dropdowns will show empty.
      }
    };

    setLoadingCountries(true);
    fetchLookups().finally(() => setLoadingCountries(false));
  }, []);

  // Fetch states when address country changes
  useEffect(() => {
    const countryId = getId(formData.countryId);
    if (!countryId) {
      setStates([]);
      setCities([]);
      return;
    }

    const fetchStates = async () => {
      setLoadingStates(true);
      try {
        const response = await axiosInstance.get(`/states/${countryId}`);
        const raw = response.data;
        const arr = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : [];
        setStates(arr);
      } catch (err) {
        console.error("Error fetching states:", err);
        setStates([]);
      } finally {
        setLoadingStates(false);
      }
    };

    fetchStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.countryId]);

  // Fetch cities when address state changes
  useEffect(() => {
    const stateId = getId(formData.stateId);
    if (!stateId) {
      setCities([]);
      return;
    }

    const fetchCities = async () => {
      setLoadingCities(true);
      try {
        const response = await axiosInstance.get(`/cities/${stateId}`);
        const raw = response.data;
        const arr = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : [];
        setCities(arr);
      } catch (err) {
        console.error("Error fetching cities:", err);
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    };

    fetchCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.stateId]);

  const toggleRole = (roleId) => {
    setSelectedRoleIds((prev) => {
      const next = prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId];
      return next;
    });
  };

  const allRolesSelected = roles.length > 0 && selectedRoleIds.length === roles.length;

  const handleToggleSelectAllRoles = () => {
    setSelectedRoleIds((prev) => {
      // If already selected all, clear; otherwise select everything.
      if (roles.length > 0 && roles.every((r) => prev.includes(parseInt(r.id, 10)))) return [];
      return roles.map((r) => parseInt(r.id, 10)).filter((id) => !Number.isNaN(id));
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: formValueWithMobileSanitize(name, value) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredText = [
      ["firstName", formData.firstName],
      ["phone", formData.phone],
      ["sourceId", formData.sourceId],
    ];

    const missing = requiredText
      .filter(([, v]) => !safeString(v).trim())
      .map(([k]) => k);
    if (missing.length) {
      alert("Please fill Name, Phone Number, and Source.");
      return;
    }
    const phoneError = mobileValidationMessage(formData.phone, { required: true, label: "Phone" });
    if (phoneError) {
      alert(phoneError);
      return;
    }
    const altError = mobileValidationMessage(formData.altPhone, { label: "Alternate phone" });
    if (altError) {
      alert(altError);
      return;
    }

    const expYears = parseDecimal(formData.experienceYears);
    if (expYears == null || expYears < 0) {
      alert("Please enter a valid number for Experience (Years).");
      return;
    }

    const expectedSal = parseDecimal(formData.expectedSalary);
    if (expectedSal == null || expectedSal < 0) {
      alert("Please enter a valid number for Expected Salary.");
      return;
    }

    const createdByNumber = getId(createdBy) ?? 1;

    const payload = new FormData();
    payload.append("First_Name", formatPascal(formData.firstName) || "");
    payload.append("Last_Name", formatPascal(formData.lastName) || "");
    payload.append("Email", formData.email);
    payload.append("Country_Code", safeString(phoneCountryCode));
    payload.append("Phone", safeString(formData.phone));
    payload.append("Alt_Phone", formData.altPhone || "");

    payload.append(
      "Qualification_Id",
      getId(formData.qualificationId) != null ? String(getId(formData.qualificationId)) : ""
    );
    payload.append("Experience_Years", String(expYears));
    payload.append("Current_Company", formData.currentCompany || "");
    payload.append(
      "Designation_Id",
      getId(formData.designationId) != null ? String(getId(formData.designationId)) : ""
    );
    payload.append("Expected_Salary", String(expectedSal));
    const currentSal = parseDecimal(formData.currentSalary);
    if (currentSal != null) {
      payload.append("Current_Salary", String(currentSal));
    }
    payload.append("Skills", formData.skills || "");
    // Backend field name is inconsistent in some environments, so send both.
    payload.append("Preferred_Location", formData.preferredLocation || "");
    payload.append("Prefered_Location", formData.preferredLocation || "");

    payload.append(
      "Source_Id",
      getId(formData.sourceId) != null ? String(getId(formData.sourceId)) : ""
    );
    payload.append(
      "Status_Id",
      getId(formData.statusId) != null ? String(getId(formData.statusId)) : ""
    );

    payload.append("Address_Line_1", formData.addressLine1 || "");
    payload.append("Address_Line_2", formData.addressLine2 || "");
    payload.append(
      "City_Id",
      getId(formData.cityId) != null ? String(getId(formData.cityId)) : ""
    );
    payload.append(
      "State_Id",
      getId(formData.stateId) != null ? String(getId(formData.stateId)) : ""
    );
    payload.append(
      "Country_Id",
      getId(formData.countryId) != null ? String(getId(formData.countryId)) : ""
    );
    payload.append("Zip_Code", formData.zipCode || "");

    payload.append("Role_Ids", JSON.stringify(selectedRoleIds));
    payload.append("Resume", resumeFile);
    payload.append("Created_By", String(createdByNumber));
    payload.append("Assigned_To", String(getId(formData.assignedTo) ?? createdByNumber));

    setSubmitting(true);
    try {
      const response = await axiosInstance.post("/create-job-lead", payload);
      const msg =
        response.data?.message ||
        response.data?.error ||
        "Job lead created successfully.";

      alert(msg);

      if (response.data?.job_lead && onAddLead) {
        onAddLead(response.data.job_lead);
      }
      if (onBack) onBack();
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to create job lead.";
      console.error("Error creating job lead:", err);
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="addlead-container job-lead-page">
      <button type="button" className="back-link" onClick={onBack}>
        <FaArrowLeft /> Back
      </button>
      <div className="issues-main-card">
      <form className="addlead-form" onSubmit={handleSubmit}>
        {/* Contact Details */}
        <div className="form-section">
          <h2 className="section-title">Contact Details</h2>
          <div className="form-grid job-lead-main-grid">
            <div className="form-group">
              <label>
                First Name <span className="required">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>
                Phone <span className="required">*</span>
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <select
                  value={phoneCountryCode}
                  onChange={(e) => setPhoneCountryCode(e.target.value)}
                  style={{ width: 80, flexShrink: 0 }}
                  required
                >
                  <option value="+91">+91</option>
                  {phoneCountryOptions
                    .filter((x) => x.code)
                    .map((c) => (
                      <option key={c.id ?? c.code} value={c.code}>
                        {c.code}
                      </option>
                    ))}
                </select>
                <input
                  style={{ flex: 1 }}
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Alt Phone</label>
              <input
                type="tel"
                name="altPhone"
                value={formData.altPhone}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>
                Source <span className="required">*</span>
              </label>
              <select
                name="sourceId"
                value={formData.sourceId}
                onChange={handleChange}
                required
              >
                <option value="">Select</option>
                {sources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                name="statusId"
                value={formData.statusId}
                onChange={handleChange}
              >
                <option value="">Select</option>
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Assigned To</label>
              <select
                name="assignedTo"
                value={formData.assignedTo}
                onChange={handleChange}
                disabled={loadingUsers}
              >
                {loadingUsers && <option value="">Loading users...</option>}
                {!loadingUsers && <option value="">Select user</option>}
                {users.map((u) => (
                  <option key={u.id} value={String(u.id)}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Role & Experience */}
        <div className="form-section">
          <h2 className="section-title">Role & Experience</h2>
          <div className="form-grid job-lead-main-grid">
            <div className="form-group">
              <label>Qualification</label>
              <select
                name="qualificationId"
                value={formData.qualificationId}
                onChange={handleChange}
              >
                <option value="">Select</option>
                {qualifications.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Experience Years</label>
              <input
                type="text"
                name="experienceYears"
                value={formData.experienceYears}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Current Company</label>
              <input
                type="text"
                name="currentCompany"
                value={formData.currentCompany}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Designation</label>
              <select
                name="designationId"
                value={formData.designationId}
                onChange={handleChange}
              >
                <option value="">Select</option>
                {designations.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Skills</label>
              <input
                type="text"
                name="skills"
                value={formData.skills}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Preferred Location</label>
              <input
                type="text"
                name="preferredLocation"
                value={formData.preferredLocation}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Compensation */}
        <div className="form-section">
          <h2 className="section-title">Compensation</h2>
          <div className="form-grid job-lead-main-grid">
            <div className="form-group">
              <label>Expected Salary</label>
              <input
                type="text"
                name="expectedSalary"
                value={formData.expectedSalary}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Current Salary</label>
              <input
                type="text"
                name="currentSalary"
                value={formData.currentSalary}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="form-section">
          <h2 className="section-title">Address</h2>
          <div className="form-grid job-lead-main-grid">
            <div className="form-group">
              <label>Address Line 1</label>
              <input
                type="text"
                name="addressLine1"
                value={formData.addressLine1}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Address Line 2</label>
              <input
                type="text"
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Country</label>
              <select
                name="countryId"
                value={formData.countryId}
                onChange={(e) => {
                  const nextCountryId = e.target.value;
                  setFormData((p) => ({
                    ...p,
                    countryId: nextCountryId,
                    stateId: "",
                    cityId: "",
                  }));
                }}
                disabled={loadingCountries}
              >
                <option value="">Select</option>
                {countries.map((c) => (
                  <option
                    key={c.country_id ?? c.Country_Id ?? c.id}
                    value={c.country_id ?? c.Country_Id ?? c.id}
                  >
                    {c.country_name || c.Country_Name || c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>State</label>
              <select
                name="stateId"
                value={formData.stateId}
                onChange={(e) => {
                  setFormData((p) => ({ ...p, stateId: e.target.value, cityId: "" }));
                }}
                disabled={loadingStates}
              >
                <option value="">{loadingStates ? "Loading..." : "Select"}</option>
                {states.map((s) => (
                  <option
                    key={s.state_id ?? s.State_Id ?? s.id}
                    value={s.state_id ?? s.State_Id ?? s.id}
                  >
                    {s.state_name || s.State_Name || s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>City</label>
              <select
                name="cityId"
                value={formData.cityId}
                onChange={(e) => setFormData((p) => ({ ...p, cityId: e.target.value }))}
                disabled={loadingCities}
              >
                <option value="">{loadingCities ? "Loading..." : "Select"}</option>
                {cities.map((c) => (
                  <option
                    key={c.city_id ?? c.City_Id ?? c.id}
                    value={c.city_id ?? c.City_Id ?? c.id}
                  >
                    {c.city_name || c.City_Name || c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Zip Code</label>
              <input
                type="text"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Roles */}
        <div className="form-section">
          <h2 className="section-title">Roles</h2>
          <div className="form-grid">
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Role</label>
              <div style={{ width: "100%", position: "relative" }}>
                <div
                  className="permissions-select-trigger"
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowRolesDropdown((o) => !o)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setShowRolesDropdown((o) => !o);
                    }
                  }}
                >
                  <span className="permissions-select-text">
                    {roles.length === 0
                      ? "Loading roles..."
                      : selectedRoleIds.length === 0
                        ? "Select Role"
                        : `${selectedRoleIds.length} role${selectedRoleIds.length > 1 ? "s" : ""} selected`}
                  </span>
                  <FaChevronDown
                    className={`permissions-select-chevron ${showRolesDropdown ? "open" : ""}`}
                  />
                </div>

                {showRolesDropdown && roles.length > 0 && (
                  <div
                    className="permissions-select-panel"
                    style={{ maxHeight: 220, overflowY: "auto" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <label className="permissions-select-item">
                      <input
                        type="checkbox"
                        checked={allRolesSelected}
                        onChange={handleToggleSelectAllRoles}
                      />
                      <span className="permissions-select-item-label">Select all</span>
                    </label>

                    {roles.map((r) => {
                      const roleId = parseInt(r.id, 10);
                      if (Number.isNaN(roleId)) return null;
                      return (
                        <label key={r.id} className="permissions-select-item">
                          <input
                            type="checkbox"
                            checked={selectedRoleIds.includes(roleId)}
                            onChange={() => toggleRole(roleId)}
                          />
                          <span className="permissions-select-item-label">{r.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Resume */}
        <div className="form-section">
          <h2 className="section-title">Resume</h2>
          <div className="form-grid">
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Resume</label>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
              />
              {resumeFile && (
                <div style={{ marginTop: 8, color: "#495057", fontSize: 13 }}>
                  Selected: {resumeFile.name}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-add-lead" disabled={submitting}>
            {submitting ? "Submitting..." : "Create Job Lead"}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}

export default AddJobLead;

